import React, { useEffect, useState } from 'react';
import { Table, Container, Spinner, Alert, Form, InputGroup, Button, Card, Modal } from 'react-bootstrap';
import { FaSearch, FaUpload, FaEdit, FaDownload } from 'react-icons/fa';
import apinode from '../../services/apinode';
import Navbar from '../../components/navbar/navbar';
import { useNavigate } from 'react-router-dom';  // <-- Importar useNavigate
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NfeListagem = () => {
  const [loadingWMS, setLoadingWMS] = useState(null);
  const [nfes, setNfes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  // Estado modal, nfe selecionada e itens da nfe
  const [mostrarModal, setMostrarModal] = useState(false);
  const [nfeSelecionada, setNfeSelecionada] = useState(null);
  const [itensNfe, setItensNfe] = useState([]);
  const [loadingItens, setLoadingItens] = useState(false);
  const [erroItens, setErroItens] = useState(null);
  const navigate = useNavigate(); // <-- Hook para navegação


  const irParaBlingNfe = () => {
    navigate('/bling/nfe');
  };


  useEffect(() => {
    async function carregarNfes() {
      try {
        setLoading(true);
        const resposta = await apinode.get(`/listar-nfe-entrada/${localStorage.idconta}`);
        setNfes(resposta.data);
      } catch (err) {
        console.error('Erro ao buscar NFEs:', err);
        setErro('Erro ao carregar notas fiscais');
      } finally {
        setLoading(false);
      }
    }

    carregarNfes();
  }, []);

  // Funções para abrir e fechar modal
  const abrirModal = async (nfe) => {
    setNfeSelecionada(nfe);
    setMostrarModal(true);
    setLoadingItens(true);
    setErroItens(null);

    try {
      // Troque o endpoint abaixo pelo correto para buscar itens da NFe
      const resposta = await apinode.get(`/itens-nfe/${nfe.id}/${localStorage.idconta}`);
      setItensNfe(resposta.data);
    } catch (err) {
      console.error('Erro ao buscar itens da NFe:', err);
      setErroItens('Erro ao carregar itens da nota fiscal.');
      setItensNfe([]);
    } finally {
      setLoadingItens(false);
    }
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setNfeSelecionada(null);
    setItensNfe([]);
    setErroItens(null);
  };

  const renderBotoesPaginacao = () => {
    const botoes = [];
    const maxBotoesVisiveis = 5;

    if (totalPaginas <= maxBotoesVisiveis + 2) {
      for (let i = 1; i <= totalPaginas; i++) {
        botoes.push(renderBotaoPagina(i));
      }
    } else {
      botoes.push(renderBotaoPagina(1));

      if (paginaAtual > 3) {
        botoes.push(<span key="start-ellipsis" className="px-2">...</span>);
      }

      const startPage = Math.max(2, paginaAtual - 1);
      const endPage = Math.min(totalPaginas - 1, paginaAtual + 1);

      for (let i = startPage; i <= endPage; i++) {
        botoes.push(renderBotaoPagina(i));
      }

      if (paginaAtual < totalPaginas - 2) {
        botoes.push(<span key="end-ellipsis" className="px-2">...</span>);
      }

      botoes.push(renderBotaoPagina(totalPaginas));
    }

    return botoes;
  };

  const renderBotaoPagina = (numero) => (
    <Button
      key={numero}
      variant={paginaAtual === numero ? 'primary' : 'outline-primary'}
      size="sm"
      onClick={() => irParaPagina(numero)}
    >
      {numero}
    </Button>
  );

  const filtrarNfes = () => {
    if (!searchTerm) return nfes;

    const termo = searchTerm.toLowerCase();
    return nfes.filter(nfe =>
      (nfe.numnfe && nfe.numnfe.toString().toLowerCase().includes(termo)) ||
      (nfe.fornec_razao && nfe.fornec_razao.toLowerCase().includes(termo)) ||
      (nfe.chavenfe && nfe.chavenfe.toLowerCase().includes(termo))
    );
  };

  const nfesFiltradas = filtrarNfes();

  // Paginação
  const indexInicial = (paginaAtual - 1) * itensPorPagina;
  const indexFinal = indexInicial + itensPorPagina;
  const nfesPaginadas = nfesFiltradas.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(nfesFiltradas.length / itensPorPagina);

  const irParaPagina = (numero) => setPaginaAtual(numero);
  const proximaPagina = () => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas));
  const paginaAnterior = () => setPaginaAtual(prev => Math.max(prev - 1, 1));

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR');
  };

  const enviarParaWMS = async (nfeId) => {
    try {
      setLoadingWMS(nfeId); // ativa o loading no botão correspondente
  
      const idconta = localStorage.idconta;
  
      if (!idconta || !nfeId) {
        toast.error('ID da conta ou da NFe está ausente.');
        return;
      }
  
      const response = await apinode.post(`/nfe-to-order/${idconta}`, {
        idnfe: nfeId
      });
  
      if (response.data.success) {
        toast.success(`NFe ID ${nfeId} enviada com sucesso para o WMS!`);
      } else {
        toast.error(`Falha ao enviar NFe ID ${nfeId}: ${response.data.error || 'Erro desconhecido'}`);
      }
  
    } catch (error) {
      console.error('Erro ao enviar NFe para WMS:', error);
  
      // Tenta extrair a mensagem da API, se existir
      const mensagemErro =
        error?.response?.data?.error || // mensagem customizada do backend
        error?.message ||               // erro genérico
        'Erro desconhecido ao enviar para o WMS.'; // fallback final
  
      toast.error(`Erro ao enviar NFe ID ${nfeId}: ${mensagemErro}`);
    } finally {
      setLoadingWMS(null); // desativa o loading
    }
  };
  


  const editarNFE = (nfe) => {
    abrirModal(nfe);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (erro) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{erro}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container-fluid mt-page">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0 text-center flex-grow-1">Notas Fiscais Importadas</h3>
          <Button variant="info" onClick={irParaBlingNfe} style={{ minWidth: '150px' }}>
            <FaDownload className="me-2" />
            Importar NFe
          </Button>
        </div>

        <Form className="mb-3">
          <InputGroup>
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Pesquisar por número, fornecedor ou chave NFe..."
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                setPaginaAtual(1);
              }}
              aria-label="Pesquisar notas fiscais"
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            />
            <Button variant="primary" onClick={() => setSearchTerm('')}>
              Limpar
            </Button>
          </InputGroup>
        </Form>

        {nfesFiltradas.length === 0 ? (
          <Alert variant="info" className="text-center">
            Nenhuma nota fiscal encontrada para o termo pesquisado.
          </Alert>
        ) : (
          <>
            <Card className="shadow-sm border-0">
              <Card.Body style={{ overflowX: 'auto' }}>
                <Table bordered hover className="align-middle" style={{ minWidth: '900px' }}>
                  <thead style={{ backgroundColor: '#f7f9fc' }}>
                    <tr>
                      <th>ID</th>
                      <th>Número NFe</th>
                      <th>Fornecedor</th>
                      <th>Data Emissão</th>
                      <th>Valor Total</th>
                      <th>Chave NFe</th>
                      <th>Status</th>
                      <th>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfesPaginadas.map(nfe => (
                      <tr key={nfe.id}>
                        <td className="text-center" style={{ fontSize: '14px' }}>{nfe.id}</td>
                        <td className="text-center" style={{ fontSize: '14px' }}>{nfe.numnfe}</td>
                        <td>{nfe.fornec_razao}</td>
                        <td className="text-center" style={{ fontSize: '14px' }}>{formatarData(nfe.dataemissao)}</td>
                        <td>{Number(nfe?.total)?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                        <td>{nfe.chavenfe}</td>
                        <td className="text-center" style={{ fontSize: '14px' }}>{nfe.status}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => enviarParaWMS(nfe.id)}
                              title="Enviar para WMS"
                              disabled={loadingWMS === nfe.id}
                            >
                              {loadingWMS === nfe.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FaUpload />
                              )}
                            </Button>

                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => editarNFE(nfe)}
                              title="Editar NFe"
                            >
                              <FaEdit />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            <div className="d-flex justify-content-center align-items-center mt-3 flex-wrap gap-3 text-center">
              <span className="text-muted" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                Página {paginaAtual} de {totalPaginas}
              </span>

              <div className="d-flex gap-1 flex-wrap">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={paginaAtual === 1}
                  onClick={paginaAnterior}
                >
                  « Anterior
                </Button>

                {totalPaginas > 1 && renderBotoesPaginacao()}

                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={paginaAtual === totalPaginas}
                  onClick={proximaPagina}
                >
                  Próxima »
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de edição - mostrar itens da NFe */}
      <Modal show={mostrarModal} onHide={fecharModal} size="lg" centered dialogClassName="custom-modalzpl">
        <Modal.Header closeButton>
          <Modal.Title>Itens da NFe #{nfeSelecionada?.numnfe}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingItens ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : erroItens ? (
            <Alert variant="danger">{erroItens}</Alert>
          ) : itensNfe.length === 0 ? (
            <Alert variant="info">Nenhum item encontrado para esta NFe.</Alert>
          ) : (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Quantidade</th>
                  <th>Valor Unitário</th>
                  <th>Valor Total</th>
                </tr>
              </thead>
              <tbody>
                {itensNfe.map(item => (
                  <tr key={item.id}>
                    <td>{item.sku}</td>
                    <td>{item.descricao}</td>
                    <td>{Number(item?.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{Number(item?.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                    <td>{Number(item?.valortotal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="text-end fw-bold">Total dos Itens:</td>
                  <td className="fw-bold">
                    {itensNfe
                      .reduce((acc, item) => acc + Number(item.valortotal || 0), 0)
                      .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </td>
                </tr>
              </tfoot>
            </Table>

          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={fecharModal}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NfeListagem;
